import React from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useDrag, useDrop } from "react-dnd";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const ITEM_TYPE = "STATUS_ROW";

const ColorSwatch = ({ color }) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: "5px",
      backgroundColor: color || "rgba(0,0,0,0.25)",
      border: "1px solid rgba(0,0,0,0.18)",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
      flexShrink: 0,
    }}
  />
);

ColorSwatch.propTypes = { color: PropTypes.string };

const StatusRow = ({
  item,
  indexWithinCategory,
  categoryKey,
  moveWithinCategory,
  moveAcrossCategories,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: ITEM_TYPE,
      item: { id: item.id, fromCategory: categoryKey, fromIndex: indexWithinCategory },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      canDrag: !item.system,
    }),
    [item.id, item.system, categoryKey, indexWithinCategory]
  );

  const [, dropRef] = useDrop(
    () => ({
      accept: ITEM_TYPE,
      hover: (dragged) => {
        if (!dragged || dragged.id === item.id) return;

        // Droppar på rad → placera före den raden.
        if (dragged.fromCategory === categoryKey) {
          if (dragged.fromIndex === indexWithinCategory) return;
          moveWithinCategory(dragged.fromIndex, indexWithinCategory);
          dragged.fromIndex = indexWithinCategory;
        } else {
          moveAcrossCategories(
            dragged.fromCategory,
            dragged.fromIndex,
            categoryKey,
            indexWithinCategory
          );
          dragged.fromCategory = categoryKey;
          dragged.fromIndex = indexWithinCategory;
        }
      },
    }),
    [item.id, categoryKey, indexWithinCategory, moveWithinCategory, moveAcrossCategories]
  );

  return (
    <Box
      ref={dropRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1,
        py: 0.25,
        borderRadius: 2,
        transition: "background-color 120ms ease",
        backgroundColor: isDragging ? "rgba(0,0,0,0.035)" : "transparent",
        "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
      }}
    >
      {/* Drag handle */}
      <Box
        ref={dragRef}
        sx={{
          width: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(0,0,0,0.42)",
          cursor: item.system ? "default" : "grab",
          opacity: item.system ? 0.25 : 0.9,
          "&:active": { cursor: item.system ? "default" : "grabbing" },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {/* Färg */}
      <ColorSwatch color={item.color} />

      {/* Text */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: item.system ? 500 : 500,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={item.name}
        >
          {item.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {item.action && item.action !== "Ingen åtgärd" ? item.action : "Ingen åtgärd"}
        </Typography>
      </Box>

      {/* Knappar */}
      <Stack direction="row" spacing={0.25} sx={{ alignItems: "center" }}>
        <Tooltip title="Flytta upp" arrow>
          <span>
            <IconButton size="small" onClick={onMoveUp} disabled={isFirst} sx={{ opacity: 0.75 }}>
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Flytta ner" arrow>
          <span>
            <IconButton size="small" onClick={onMoveDown} disabled={isLast} sx={{ opacity: 0.75 }}>
              <ArrowDownwardIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Redigera" arrow>
          <span>
            <IconButton size="small" onClick={onEdit} sx={{ opacity: 0.85 }}>
              <EditIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={item.system ? "Systemstatus kan inte tas bort" : "Ta bort"} arrow>
          <span>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={item.system}
              sx={{ opacity: item.system ? 0.2 : 0.75 }}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

StatusRow.propTypes = {
  item: PropTypes.object.isRequired,
  indexWithinCategory: PropTypes.number.isRequired,
  categoryKey: PropTypes.string.isRequired,
  moveWithinCategory: PropTypes.func.isRequired,
  moveAcrossCategories: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
};

export default StatusRow;
